import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "auto";

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Сохранённая тема пользователя, иначе светлая по умолчанию.
  // Важно: НЕ 'auto' — в Telegram WebView системная тема часто тёмная,
  // из-за чего приложение открывалось тёмным при первом входе.
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem("theme") as Theme;
    return saved || "light";
  });

  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  // Функция для определения темы с учётом 'auto'
  const getResolvedTheme = (theme: Theme): "light" | "dark" => {
    if (theme === "auto") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme;
  };

  // Обновляем тему
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  // Переключение между светлой/тёмной по фактически применённой теме
  // (resolvedTheme), чтобы одно нажатие всегда инвертировало видимую тему
  // даже если текущее значение — 'auto'.
  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  // Применяем тему к document
  useEffect(() => {
    const resolved = getResolvedTheme(theme);
    setResolvedTheme(resolved);

    document.documentElement.setAttribute("data-theme", resolved);
    document.documentElement.classList.toggle(
      "dark-theme",
      resolved === "dark",
    );

    // Слушаем изменения системной темы
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "auto") {
        const resolved = getResolvedTheme("auto");
        setResolvedTheme(resolved);
        document.documentElement.setAttribute("data-theme", resolved);
        document.documentElement.classList.toggle(
          "dark-theme",
          resolved === "dark",
        );
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, setTheme, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Хук для использования темы
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
